/**
 * 主尊阿弥陀佛立像：图像锚点、各光源参数、佛像布局与剪影遮罩。
 *
 * 光源分为五路，各自独立开关、调节，并分别取一路音频特征驱动：
 *   头部圆光（以额头为圆心，头后多层圆环 + 少量宽柔远伸光线）
 *   周身金白光（沿身形向外舒展的层次光明）
 *   佛身轮廓光（贴着剪影外缘的柔和光边 + 内缘透光）
 *   眉间白毫柔光（白毫处的柔和光辉，非定向光束）
 *   佛顶光（肉髻上方的光晕，可选「佛顶放光」）
 */

import { merge } from './look';
import type { DeepPartial } from './types';

/* ------------------------------------------------------------------ */
/* 图像与锚点（以图像宽高归一化，原图 849 × 1852）                       */
/* ------------------------------------------------------------------ */

export const IMG_W = 849;
export const IMG_H = 1852;
export const IMG_ASPECT = IMG_W / IMG_H;

/** 额头中心（圆光圆心）：发际线约 y=136，白毫 y=164 */
export const ANCHOR_FOREHEAD = { x: 425 / IMG_W, y: 152 / IMG_H };
/** 眉间白毫：经逐像素定位，原图 (425, 164) */
export const ANCHOR_URNA = { x: 425 / IMG_W, y: 164 / IMG_H };
/** 佛顶（肉髻顶端约 y=33，光晕中心略低） */
export const ANCHOR_CROWN = { x: 425 / IMG_W, y: 40 / IMG_H };
/** 周身光的光心（胸口） */
export const ANCHOR_BODY = { x: 425 / IMG_W, y: 0.42 };

/* ------------------------------------------------------------------ */
/* 参数                                                                */
/* ------------------------------------------------------------------ */

/** 驱动来源：综合驱动 / 响度包络 / 四个频段 */
export type LightSource = 'drive' | 'loud' | 'low' | 'lowmid' | 'highmid' | 'high';

export const SOURCE_OPTIONS: { label: string; value: LightSource }[] = [
  { label: '综合驱动（随音频驱动设置）', value: 'drive' },
  { label: '响度包络', value: 'loud' },
  { label: '低频 30–250Hz', value: 'low' },
  { label: '中低 250–1kHz', value: 'lowmid' },
  { label: '中高 1–4kHz（人声清晰度）', value: 'highmid' },
  { label: '高频 4–12kHz', value: 'high' },
];

export interface BuddhaConfig {
  /** 佛像大小：1 = 在可用区域内最大且完整；缩小时以佛像中心为基准 */
  size: number;
  head: {
    on: boolean;
    /** 圆光半径（佛像高度的比例） */
    radius: number;
    intensity: number;
    /** 圆环层次（同心圆环的清晰度） */
    rings: number;
    /** 圆心上下微调（佛像高度的比例，正数向下） */
    offsetY: number;
    color: string;
    /** 远伸光线 */
    rayCount: number;
    rayIntensity: number;
    rayWidth: number;
    rayLength: number;
    source: LightSource;
    react: number;
  };
  aura: {
    on: boolean;
    intensity: number;
    /** 舒展范围 */
    spread: number;
    /** 光流（宽柔的流动光带） */
    flow: number;
    /** 身内透光：佛身自身的柔和金光，过高会洗掉衣饰细节 */
    inner: number;
    color: string;
    source: LightSource;
    react: number;
  };
  contour: {
    on: boolean;
    intensity: number;
    /** 光边宽度 */
    width: number;
    /** 内缘透光（光从身后漫过边缘） */
    inner: number;
    color: string;
    source: LightSource;
    react: number;
  };
  urna: {
    on: boolean;
    intensity: number;
    size: number;
    color: string;
    source: LightSource;
    react: number;
  };
  crown: {
    on: boolean;
    intensity: number;
    size: number;
    /** 佛顶放光：向上舒展的柔和光束，0 = 关闭 */
    beam: number;
    color: string;
    source: LightSource;
    react: number;
  };
}

export const BUDDHA_DEFAULTS: BuddhaConfig = {
  size: 1,
  head: {
    on: true,
    radius: 0.155,
    intensity: 1.1,
    rings: 0.85,
    offsetY: 0,
    color: '#ffe3a0',
    rayCount: 12,
    rayIntensity: 0.4,
    rayWidth: 1,
    rayLength: 0.9,
    source: 'drive',
    react: 0.8,
  },
  aura: {
    on: true,
    intensity: 0.75,
    spread: 1,
    flow: 0.45,
    inner: 0.12,
    color: '#ffe6b0',
    source: 'loud',
    react: 0.7,
  },
  contour: {
    on: true,
    intensity: 0.9,
    width: 0.45,
    inner: 0.4,
    color: '#fff0c8',
    source: 'loud',
    react: 0.8,
  },
  urna: {
    on: true,
    intensity: 0.75,
    size: 1,
    color: '#fffaf0',
    source: 'highmid',
    react: 0.7,
  },
  crown: {
    on: true,
    intensity: 0.55,
    size: 1,
    beam: 0,
    color: '#fff1cc',
    source: 'drive',
    react: 0.6,
  },
};

const STORE_KEY = 'nianfo.buddha.v1';

export function loadBuddhaConfig(): BuddhaConfig {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return merge(BUDDHA_DEFAULTS, raw ? (JSON.parse(raw) as DeepPartial<BuddhaConfig>) : undefined);
  } catch {
    return structuredClone(BUDDHA_DEFAULTS);
  }
}

export function saveBuddhaConfig(cfg: BuddhaConfig): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(cfg));
  } catch {
    /* 隐私模式下忽略 */
  }
}

/* ------------------------------------------------------------------ */
/* 布局                                                                */
/* ------------------------------------------------------------------ */

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FitOptions {
  /** 顶部保留（安全区 + 边距），px */
  top: number;
  /** 底部保留（控制栏或首屏入口），px */
  bottom: number;
  /** 左右最小边距，px */
  side: number;
  /** 0.3..1 */
  size: number;
  /** 圆光高出图像顶端的部分（佛像高度的比例） */
  headroom: number;
}

/**
 * 计算佛像显示框：水平居中、完整呈现（含圆光顶部），在可用区域内尽可能大并靠上；
 * size < 1 时以最大框的中心为基准缩小，中心位置不变。
 */
export function fitBuddha(vw: number, vh: number, o: FitOptions): Box {
  const availH = Math.max(40, vh - o.top - o.bottom);
  const availW = Math.max(40, vw - 2 * o.side);
  const hMax = Math.max(20, Math.min(availH / (1 + o.headroom), availW / IMG_ASPECT));
  const wMax = hMax * IMG_ASPECT;
  // 宽度受限时（极窄长屏）剩余高度放在佛像下方，保持「佛像居上」
  const spare = availH - hMax * (1 + o.headroom);
  const yMax = o.top + hMax * o.headroom + Math.min(spare, Math.max(0, spare) * 0.25);
  const cx = vw / 2;
  const cy = yMax + hMax / 2;
  const s = Math.min(1, Math.max(0.3, o.size));
  const h = hMax * s;
  const w = wMax * s;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

/** 圆光高出图像顶端的部分 */
export function haloHeadroom(cfg: BuddhaConfig): number {
  if (!cfg.head.on) return 0.012;
  return Math.max(0.012, cfg.head.radius * 1.04 - (ANCHOR_FOREHEAD.y + cfg.head.offsetY) + 0.012);
}

/* ------------------------------------------------------------------ */
/* 剪影遮罩：模糊层（供 GL 光效）、每行左右边界（供歌词避让）             */
/* ------------------------------------------------------------------ */

/** 遮罩四周外扩（以图像宽度为单位），给外发光留出空间 */
export const MASK_PAD = 0.32;

export interface BuddhaMask {
  /** 遮罩纹理尺寸 */
  width: number;
  height: number;
  /** RGBA：R=清晰剪影 G=小模糊 B=中模糊 A=大模糊 */
  data: Uint8Array;
  /** 外扩比例（相对图像宽 / 高） */
  padU: number;
  padV: number;
  /** 每行剪影左右边界（图像宽度比例），行数 = rows；无像素的行为 [0.5, 0.5] */
  rows: number;
  left: Float32Array;
  right: Float32Array;
  /** 内缘光（白色 + alpha），用于前景叠加 */
  rimCanvas: HTMLCanvasElement;
  /** 佛身剪影（白色 + alpha），用于身内透光 */
  bodyCanvas: HTMLCanvasElement;
}

/** 三次盒式模糊 ≈ 高斯模糊（单通道，原地） */
export function blurChannel(src: Float32Array, w: number, h: number, radius: number): Float32Array {
  let a = src.slice();
  let b = new Float32Array(src.length);
  const r = Math.max(1, Math.round(radius / Math.sqrt(3)));
  for (let pass = 0; pass < 3; pass++) {
    // 水平
    for (let y = 0; y < h; y++) {
      let acc = 0;
      const row = y * w;
      for (let x = -r; x <= r; x++) acc += a[row + Math.min(w - 1, Math.max(0, x))];
      for (let x = 0; x < w; x++) {
        b[row + x] = acc / (2 * r + 1);
        acc += a[row + Math.min(w - 1, x + r + 1)] - a[row + Math.max(0, x - r)];
      }
    }
    // 垂直
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let y = -r; y <= r; y++) acc += b[Math.min(h - 1, Math.max(0, y)) * w + x];
      for (let y = 0; y < h; y++) {
        a[y * w + x] = acc / (2 * r + 1);
        acc += b[Math.min(h - 1, y + r + 1) * w + x] - b[Math.max(0, y - r) * w + x];
      }
    }
  }
  return a;
}

export function buildBuddhaMask(img: HTMLImageElement): BuddhaMask {
  // 遮罩分辨率：图像宽 300px 左右即可（光效本身是柔和的）
  const scale = 300 / img.naturalWidth;
  const iw = Math.round(img.naturalWidth * scale);
  const ih = Math.round(img.naturalHeight * scale);
  const px = Math.round(iw * MASK_PAD);
  const py = Math.round(iw * MASK_PAD);
  const W = iw + 2 * px;
  const H = ih + 2 * py;
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, px, py, iw, ih);
  const id = ctx.getImageData(0, 0, W, H).data;
  const alpha = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) alpha[i] = id[i * 4 + 3] / 255;

  // 模糊半径以原图像素计：小 ≈ 10px、中 ≈ 38px、大 ≈ 120px
  const k = scale;
  const s = blurChannel(alpha, W, H, 10 * k);
  const m = blurChannel(alpha, W, H, 38 * k);
  const l = blurChannel(alpha, W, H, 120 * k);
  const data = new Uint8Array(W * H * 4);
  for (let i = 0; i < W * H; i++) {
    data[i * 4] = Math.round(alpha[i] * 255);
    data[i * 4 + 1] = Math.round(Math.min(1, s[i]) * 255);
    data[i * 4 + 2] = Math.round(Math.min(1, m[i]) * 255);
    data[i * 4 + 3] = Math.round(Math.min(1, l[i]) * 255);
  }

  // 每行左右边界（只看图像区域）
  const rows = ih;
  const left = new Float32Array(rows);
  const right = new Float32Array(rows);
  for (let y = 0; y < ih; y++) {
    let lo = -1;
    let hi = -1;
    const row = (y + py) * W;
    for (let x = 0; x < iw; x++) {
      if (alpha[row + px + x] > 0.18) {
        if (lo < 0) lo = x;
        hi = x;
      }
    }
    left[y] = lo < 0 ? 0.5 : lo / iw;
    right[y] = hi < 0 ? 0.5 : (hi + 1) / iw;
  }

  // 内缘光：剪影内侧靠近边缘处最亮，向内迅速消退（只取图像区域）
  const rimCanvas = document.createElement('canvas');
  const bodyCanvas = document.createElement('canvas');
  rimCanvas.width = bodyCanvas.width = iw;
  rimCanvas.height = bodyCanvas.height = ih;
  const rc = rimCanvas.getContext('2d')!;
  const bc = bodyCanvas.getContext('2d')!;
  const rim = rc.createImageData(iw, ih);
  const body = bc.createImageData(iw, ih);
  const s2 = blurChannel(alpha, W, H, 18 * k);
  for (let y = 0; y < ih; y++) {
    for (let x = 0; x < iw; x++) {
      const i = (y + py) * W + px + x;
      const o = (y * iw + x) * 4;
      const a = alpha[i];
      const edge = Math.max(0, Math.min(1, (1 - s2[i]) * 2.4));
      rim.data[o] = rim.data[o + 1] = rim.data[o + 2] = 255;
      rim.data[o + 3] = Math.round(a * edge * 255);
      body.data[o] = body.data[o + 1] = body.data[o + 2] = 255;
      body.data[o + 3] = Math.round(a * 255);
    }
  }
  rc.putImageData(rim, 0, 0);
  bc.putImageData(body, 0, 0);

  return { width: W, height: H, data, padU: px / iw, padV: py / ih, rows, left, right, rimCanvas, bodyCanvas };
}

/** 佛像在 [y0, y1]（屏幕 px）范围内剪影的最右 / 最左边界（屏幕 px） */
export function silhouetteExtent(mask: Pick<BuddhaMask, 'rows' | 'left' | 'right'> | null, box: Box, y0: number, y1: number): { left: number; right: number } {
  if (!mask) return { left: box.x, right: box.x + box.w };
  const r0 = Math.floor(((y0 - box.y) / box.h) * mask.rows);
  const r1 = Math.ceil(((y1 - box.y) / box.h) * mask.rows);
  let lo = Infinity;
  let hi = -Infinity;
  for (let r = Math.max(0, r0); r <= Math.min(mask.rows - 1, r1); r++) {
    if (mask.left[r] === 0.5 && mask.right[r] === 0.5) continue;
    lo = Math.min(lo, mask.left[r]);
    hi = Math.max(hi, mask.right[r]);
  }
  if (hi < lo) return { left: box.x + box.w / 2, right: box.x + box.w / 2 };
  return { left: box.x + lo * box.w, right: box.x + hi * box.w };
}

/* ------------------------------------------------------------------ */
/* 音频 → 各光源驱动量                                                  */
/* ------------------------------------------------------------------ */

export interface LightLevels {
  head: number;
  aura: number;
  contour: number;
  urna: number;
  crown: number;
  /** 圆光涟漪 / 白毫脉冲 */
  pulse: number;
}

export interface FeatureView {
  drive: number;
  loudness: number;
  bands: [number, number, number, number];
}

export function sourceValue(f: FeatureView, src: LightSource): number {
  switch (src) {
    case 'loud':
      return f.loudness;
    case 'low':
      return f.bands[0];
    case 'lowmid':
      return f.bands[1];
    case 'highmid':
      return f.bands[2];
    case 'high':
      return f.bands[3];
    default:
      return f.drive;
  }
}

/**
 * 光源亮度系数：静默时略暗（1 − 0.35·响应），声音饱满时增强到（1 + 0.8·响应），
 * 起音 / 换句脉冲再叠加一点。未播放时 v 为缓慢呼吸值。
 */
export function lightLevel(base: number, react: number, v: number, pulse: number, pulseGain: number): number {
  const vv = Math.min(1.4, Math.max(0, v));
  return Math.max(0, base * (1 - 0.35 * react + 1.15 * react * vv + pulseGain * react * pulse));
}
